import * as React from 'react';
import cn from 'classnames';
import styled from 'styled-components';
import { withTranslation, WithTranslation } from 'react-i18next';

import { ProductsContext } from 'Jsx/ContextApis/Products';
import InverseButton from 'crehana-ui/Buttons/InverseButton';
import PrimaryButton from 'crehana-ui/Buttons/PrimaryButton';
import IconButton from 'crehana-ui/Buttons/IconButton';
import { InverseButtonProps } from 'crehana-ui/Buttons/InverseButton/types';
import {
  primaryMain,
  baseLighter,
  purpleLighter,
  purple,
  white,
} from 'crehana-ui/styles/colors';
import userActivity from 'Jsx/Utils/userActivity';
import buyProduct, { trackProduct } from 'Jsx/Utils/buyProduct';

import ShoppingCartLight from 'crehana-icons/ShoppingCartLight';
import ShoppingCartAdded from 'crehana-icons/ShoppingCartAdded';

import { CourseCardProps } from 'Jsx/Shared/CourseCardV3/types';
import EVENTS from 'Jsx/Shared/Tracking/events';
import {
  eventSources,
  eventNames,
  setFbEventToLocalStorage,
} from 'Jsx/Utils/facebookTracking/facebookTracking';

// Created to customize the hover styles
const StyledButton = styled<
  React.FC<
    InverseButtonProps & {
      rel?: string;
      testid?: string;
    }
  >
>(
  ({
    full,
    size,
    className,
    onClick,
    href,
    label,
    disabled,
    testid,
    white,
  }) => (
    <InverseButton
      disabled={disabled}
      data-testid={testid}
      href={href}
      onClick={onClick}
      full={full}
      white={white}
      size={size}
      className={cn(className)}
    >
      <span
        className={`font-gil font-bold text-14 leading-16 tracking-4 ${
          white ? 'text-purple-lighter' : 'text-primary-main'
        }`}
      >
        {label}
      </span>
    </InverseButton>
  ),
)`
  padding-right: 0 !important;
  padding-left: 0 !important;
  transform: translate(0, 0);
  transition: all 0.2s ease-out !important;
  ${({ full }) => (full ? 'width: 100%;' : 'width: calc(100% - 64px);')}
  ${({ white }) => {
    if (white) {
      return `
        span {
          color: white;
        }
      `;
    }
  }}
  &:hover {
    span {
      ${({ white }) => (white ? `color: ${purple}` : '')}
    }
    background: white;
    transform: translate(0, -2px);
    box-shadow: 0px 2px 6px rgba(92, 84, 122, 0.2);
  }
`;

StyledButton.defaultProps = {
  rel: 'no-follow',
  full: true,
  size: 'm',
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const StyledIconButton = styled(({ inShoppingCart, darkMode, ...rest }) => (
  <IconButton {...rest} />
))<{
  inShoppingCart: boolean | undefined;
}>`
  border-color: ${props => (props.darkMode ? white : primaryMain)} !important;
  ${props =>
    props.inShoppingCart &&
    `
      background: ${primaryMain};
    `}
  @media (min-width: 1024px) {
    transform: translate(0, 0);
    transition: all 0.2s ease-out !important;
    &:hover {
      transform: translate(0, -2px);
      -webkit-box-shadow: 0px 2px 6px rgba(92, 84, 122, 0.2);
      box-shadow: 0px 2px 6px rgba(92, 84, 122, 0.2);
    }
  }
`;

class AddToCart extends React.Component<
  Partial<CourseCardProps> & WithTranslation
> {
  handleAddToCart = () => {
    const { addCourseToCart, inShoppingCart, originalId } = this.props;
    const buyUrl = `/shopping-cart/course/${originalId}`;

    if (inShoppingCart) {
      return this.removeCourseFromCartWithTrack();
    }
    if (addCourseToCart) {
      return this.addCourseToCartWithTrack();
    }

    window.location.href = buyUrl;
  };

  getMixPanelData = () => {
    const { originalId, title, itemTypeTrack, courseCardPage } = this.props;
    const abTest =
      typeof window !== 'undefined' &&
      window.__INITIAL_STATE__ &&
      window.__INITIAL_STATE__.ab_tests;

    return {
      itemType: itemTypeTrack || 'COURSE',
      currentPage: courseCardPage,
      originalId,
      title,
      /*
        TODO: move abTest data to mixPanel options (mixpanel.html)
      */
      ...(abTest
        ? Object.keys(abTest).reduce((obj, next) => {
            obj[`ab_test_${next}`] = abTest[next]; // eslint-disable-line no-param-reassign

            return obj;
          }, {})
        : {}),
    };
  };

  removeCourseFromCartWithTrack = () => {
    const { removeCourseFromCart, originalId } = this.props;
    const mixPanelData = this.getMixPanelData();

    window.track(EVENTS.REMOVE_FROM_CART, mixPanelData);
    if (removeCourseFromCart) {
      removeCourseFromCart({
        courseId: originalId as number,
      });
    }
  };

  addCourseToCartWithTrack = () => {
    const { addCourseToCart, originalId } = this.props;
    const mixPanelData = this.getMixPanelData();

    window.track(EVENTS.ADD_TO_CART, mixPanelData);
    if (addCourseToCart) {
      addCourseToCart({
        courseId: originalId as number,
      });
    }
  };

  handleDirectBuyButton = () => {
    const {
      urlToBuyNow,
      courseCardPage,
      originalId,
      onClickBuyNow,
      product,
      beforeBuy,
      prices,
      title,
    } = this.props;

    if (beforeBuy) {
      beforeBuy();
    }

    setTimeout(() => {
      window.track(EVENTS.DIRECT_BUY, {
        source: courseCardPage || 'catalog',
      });

      const fbTrackPayload = {
        content_name: title,
        content_ids: originalId,
        content_type: 'course',
        value: prices?.price,
        currency: prices?.currencySymbol,
      };

      if (onClickBuyNow) {
        trackProduct(product);
        onClickBuyNow({
          itemId: originalId as number,
          itemType: 'course',
          itemData: {
            itemId: originalId || 0,
            itemType: 'course',
          },
        });
      } else if (product && urlToBuyNow) {
        setFbEventToLocalStorage({
          eventName: eventNames.GO_TO_CHECKOUT,
          sourcePage: eventSources.COURSE_CARD,
          fbTrackPayload,
        });
        buyProduct(urlToBuyNow, product);
      } else {
        /* TODO: This should be deprecated soon. We should only buy products using `buyProduct` function.
         But we need all the course card receive a `product` property */
        setFbEventToLocalStorage({
          eventName: eventNames.GO_TO_CHECKOUT,
          sourcePage: eventSources.COURSE_CARD,
          fbTrackPayload,
        });
        userActivity.track(
          userActivity.EVENTS.CLICK,
          {
            item_id: originalId,
            item_name: 'CourseCardV3',
            item_type: 'course',
            item_path: location.pathname,
          },
          urlToBuyNow,
        );
      }
    }, 0);
  };

  render() {
    const {
      inShoppingCart,
      prices,
      loadingCart,
      courseType,
      isEnrollment,
      originalId,
      canEnroll,
      pageType,
      enrollmentAccessPreviewUrl,
      showOnlyAddToCardButton,
      showOnlyShoppingCartButton,
      isBundle,
      enrollmentUrl,
      t,
      showBuyNowButtonOnly,
      onClickEnrollNow,
      url,
      dark,
      user,
      pageId,
      temporalAccessDurationLabel,
    } = this.props;
    const buyUrl = `/shopping-cart/course/${originalId}`;

    const courseEnrollmentUrl =
      (prices && prices.price === 0) || canEnroll
        ? `/enrollment/${originalId}/`
        : buyUrl;

    const classesUrl = `/clases/v2/${originalId}/`;

    const ClassicAddToCartBtn = (
      <React.Fragment>
        {this.props.showAddToCartButtonOnly ? (
          <div className="pt-16">
            <InverseButton
              full
              label={t('ADD_ITEM', {
                defaultValue: 'Agregar al carrito	',
              })}
              onClick={this.handleAddToCart}
              leftIcon={{
                component: inShoppingCart
                  ? ShoppingCartAdded
                  : ShoppingCartLight,
                size: 24,
              }}
              className="pr-0 pl-0"
              disabled={loadingCart}
              white={dark}
            />
          </div>
        ) : (
          <div className="AddToCartComponent flex items-center justify-between pt-16">
            <StyledIconButton
              onClick={this.handleAddToCart}
              customIcon={
                inShoppingCart ? ShoppingCartAdded : ShoppingCartLight
              }
              customColor={
                dark ? white : inShoppingCart ? purpleLighter : primaryMain
              }
              customSize={inShoppingCart ? 20 : 24}
              disabled={loadingCart}
              inShoppingCart={inShoppingCart}
              darkMode={dark}
            />
            <StyledButton
              onClick={this.handleDirectBuyButton}
              full={false}
              label={t('BUY_NOW', {
                defaultValue: 'Comprar ahora',
              })}
              white={dark}
            />
          </div>
        )}
      </React.Fragment>
    );

    const FullAddToCardButton = (
      <PrimaryButton
        css={`
          ${inShoppingCart ? `background-color: ${baseLighter} !important` : ''}
        `}
        onClick={this.handleAddToCart}
        disabled={loadingCart}
        leftIcon={{
          component: inShoppingCart ? ShoppingCartAdded : ShoppingCartLight,
          size: 24,
        }}
        label={
          inShoppingCart
            ? t('REMOVE_FROM_CART', {
                defaultValue: 'Quitar del carrito',
              })
            : t('ADD_ITEM', {
                defaultValue: 'Agregar al carrito	',
              })
        }
        size="m"
        className="w-full mt-16"
      />
    );

    if (isEnrollment) {
      return (
        <StyledButton
          full
          className="mt-16"
          href={classesUrl}
          testId="watch-course"
          label={t('GO_TO_COURSE', {
            defaultValue: 'Ir al curso',
          })}
          white={dark}
        />
      );
    }

    if (pageType === 'COURSE TEMPORAL ACCESS') {
      return (
        <ProductsContext.Consumer>
          {({ setBasicDataForm }) => (
            <StyledButton
              full
              className="mt-16"
              onClick={() => {
                const freeEnrollmentUrl = `/enrollment/temporal-access/${pageId}/${originalId}/`;

                if (user && user.originalId) {
                  if (user.phoneNumber) {
                    window.location.href = freeEnrollmentUrl;
                  } else {
                    setBasicDataForm({
                      isOpen: true,
                      redirectUrl: freeEnrollmentUrl,
                    });
                  }
                } else {
                  window.showRegisterModal(
                    freeEnrollmentUrl,
                    {},
                    {
                      shouldRequireExtraData: true,
                    },
                  );
                }
              }}
              testId="watch-course"
              label={
                temporalAccessDurationLabel || 'Acceso gratuito por 24 horas'
              }
              white={dark}
            />
          )}
        </ProductsContext.Consumer>
      );
    }

    if (canEnroll) {
      if (courseType === 'temporal-access') {
        return (
          <StyledButton
            full
            className="mt-16"
            onClick={() => {
              if (user && user.originalId) {
                window.location.href = courseEnrollmentUrl;
              } else {
                window.showRegisterModal(courseEnrollmentUrl);
              }
            }}
            testId="watch-course"
            label="Acceso gratuito por 24 horas"
            white={dark}
          />
        );
      }
      if (pageType === 'COURSE_ACCESS_PREVIEW' && courseType === 'freemium') {
        return (
          <StyledButton
            href={enrollmentAccessPreviewUrl}
            full
            className="mt-16"
            testId="watch-course"
            label={t('TRY_COURSE', {
              defaultValue: '¡Pruébalo ahora!',
            })}
            white={dark}
          />
        );
      }

      if (courseType === 'free') {
        return (
          <ProductsContext.Consumer>
            {({ setBasicDataForm }) => (
              <StyledButton
                full
                className="mt-16"
                testId="course-enrollement-button-free"
                onClick={() => {
                  if (onClickEnrollNow) {
                    onClickEnrollNow();
                  } else if (user && user.originalId) {
                    if (user.phoneNumber) {
                      window.location.href = isBundle
                        ? enrollmentUrl
                        : courseEnrollmentUrl;
                    } else {
                      setBasicDataForm({
                        isOpen: true,
                        redirectUrl: isBundle
                          ? enrollmentUrl
                          : courseEnrollmentUrl,
                      });
                    }
                  } else {
                    window.showRegisterModal(
                      isBundle ? enrollmentUrl : courseEnrollmentUrl,
                      {},
                      {
                        shouldRequireExtraData: true,
                      },
                    );
                  }
                }}
                label={t('SUBSCRIBE_FREE', {
                  defaultValue: '¡Inscríbete gratis!',
                })}
                white={dark}
              />
            )}
          </ProductsContext.Consumer>
        );
      }

      if (courseType === 'membership') {
        return (
          <StyledButton
            className="mt-16"
            full
            href={isBundle ? enrollmentUrl : courseEnrollmentUrl}
            label="Acceso Premium"
            white={dark}
          />
        );
      }

      return (
        <StyledButton
          full
          className="mt-16"
          onClick={() => {
            if (onClickEnrollNow) {
              onClickEnrollNow();
            } else {
              window.location.href = isBundle
                ? enrollmentUrl
                : courseEnrollmentUrl;
            }
          }}
          label={t('SUBSCRIBE_NOW', {
            defaultValue: '¡Inscríbete ahora!',
          })}
          white={dark}
        />
      );
    }

    if (courseType === 'membership') {
      return (
        <StyledButton
          className="mt-16"
          full
          href={url}
          label="Acceso Premium"
          white={dark}
        />
      );
    }

    /**
     * Guys! We need to refactor this conditions. Actually we have 3 use-cases
     * 1- The course card with the two buttons, add to card and buy now
     * 2- The course card with one button, the add-to-cart (shoppingCart)
     * 3- The course card with one button, the buy-now.
     * We need a refactor because it's complicated to use the current prop
     * <CourseCard
     *  showBuyNowButtonOnly
     *  showOnlyShoppingCartButton
     * />
     * What the dev would expect?
     * Maybe we can add a new prop? ButtonGroupType: 1 | 2 | 3 ?
     */
    if (showBuyNowButtonOnly) {
      return (
        <PrimaryButton
          onClick={this.handleDirectBuyButton}
          className="mt-16"
          full
          label={t('BUY_NOW', {
            defaultValue: 'Comprar ahora',
          })}
        />
      );
    }

    return showOnlyAddToCardButton || showOnlyShoppingCartButton
      ? FullAddToCardButton
      : ClassicAddToCartBtn;
  }
}

export default withTranslation('main-menu')(AddToCart);
